import {
  BANK_FORMAT_KEYS,
  BANK_ITEM_KINDS,
} from 'scenes/ProjectBank/utils/bankItem';

/** Sortable columns; the list header maps its cells onto these. */
export const BANK_SORT_COLUMNS = {
  NAME: 'name',
  DATE: 'date',
  CREATOR: 'creator',
  TYPE: 'type',
  SIZE: 'size',
};

const ASCENDING = 'ascending';
const DESCENDING = 'descending';

/** The 11 sort menu entries shared by the toolbar, list header and picker. */
export const BANK_SORT_OPTIONS = [
  { key: '', labelKey: 'bank.sort_default', column: null, direction: null },
  {
    key: 'name-ascending',
    labelKey: 'bank.name_ascending',
    column: BANK_SORT_COLUMNS.NAME,
    direction: ASCENDING,
  },
  {
    key: 'name-descending',
    labelKey: 'bank.name_descending',
    column: BANK_SORT_COLUMNS.NAME,
    direction: DESCENDING,
  },
  {
    key: 'date-ascending',
    labelKey: 'bank.date_ascending',
    column: BANK_SORT_COLUMNS.DATE,
    direction: ASCENDING,
  },
  {
    key: 'date-descending',
    labelKey: 'bank.date_descending',
    column: BANK_SORT_COLUMNS.DATE,
    direction: DESCENDING,
  },
  {
    key: 'creator-ascending',
    labelKey: 'bank.creator_ascending',
    column: BANK_SORT_COLUMNS.CREATOR,
    direction: ASCENDING,
  },
  {
    key: 'creator-descending',
    labelKey: 'bank.creator_descending',
    column: BANK_SORT_COLUMNS.CREATOR,
    direction: DESCENDING,
  },
  {
    key: 'type-ascending',
    labelKey: 'bank.type_ascending',
    column: BANK_SORT_COLUMNS.TYPE,
    direction: ASCENDING,
  },
  {
    key: 'type-descending',
    labelKey: 'bank.type_descending',
    column: BANK_SORT_COLUMNS.TYPE,
    direction: DESCENDING,
  },
  {
    key: 'size-ascending',
    labelKey: 'bank.size_ascending',
    column: BANK_SORT_COLUMNS.SIZE,
    direction: ASCENDING,
  },
  {
    key: 'size-descending',
    labelKey: 'bank.size_descending',
    column: BANK_SORT_COLUMNS.SIZE,
    direction: DESCENDING,
  },
];

const OPTIONS_BY_KEY = BANK_SORT_OPTIONS.reduce((map, option) => {
  map[option.key] = option;
  return map;
}, {});

/**
 * The order the bank opens in as the project's gallery (the Galleria
 * button): newest first, as a gallery reads.
 */
export const GALLERY_VIEW_SORT_BY = 'date-descending';

// Where no uploader is shown there is nobody to sort by.
const OPTIONS_WITHOUT_CREATOR = BANK_SORT_OPTIONS.filter(
  (option) => option.column !== BANK_SORT_COLUMNS.CREATOR
);

/**
 * Parses a sortBy key ('name-ascending') into its column and direction.
 * @param {string} sortBy
 * @returns {{column: string, direction: string}|null} null for '' or unknown keys
 */
export function parseSortBy(sortBy) {
  const option = typeof sortBy === 'string' ? OPTIONS_BY_KEY[sortBy] : null;
  if (!option || !option.column) return null;
  return { column: option.column, direction: option.direction };
}

/**
 * The sort menu's entries: every BANK_SORT_OPTIONS entry, or, while no
 * uploader is shown (a gallery-only user without "view media adder info"),
 * all but the creator sorts. One constant array per case.
 * @param {{hideAuthors?: boolean}} [options]
 * @returns {Array<Object>} BANK_SORT_OPTIONS entries
 */
export function getBankSortOptions({ hideAuthors = false } = {}) {
  return hideAuthors ? OPTIONS_WITHOUT_CREATOR : BANK_SORT_OPTIONS;
}

/**
 * The sort key the bank orders by: `sortBy` itself, except that a creator
 * sort falls back to the default ('') while no uploader is shown, where the
 * menus do not offer it and every author is hidden.
 * @param {string} sortBy stored sort key
 * @param {{hideAuthors?: boolean}} [options]
 * @returns {string}
 */
export function resolveBankSortBy(sortBy, { hideAuthors = false } = {}) {
  const parsed = parseSortBy(sortBy);
  return hideAuthors && parsed && parsed.column === BANK_SORT_COLUMNS.CREATOR
    ? ''
    : sortBy;
}

/**
 * Sort key produced by clicking a list header column: the same column flips
 * the direction; a new column starts ascending, except 'date' which starts
 * descending (newest first).
 * @param {string} currentSortBy
 * @param {string} column one of BANK_SORT_COLUMNS
 * @returns {string} next sortBy key
 */
export function nextSortForColumn(currentSortBy, column) {
  const current = parseSortBy(currentSortBy);
  if (current && current.column === column) {
    return `${column}-${
      current.direction === ASCENDING ? DESCENDING : ASCENDING
    }`;
  }
  return `${column}-${
    column === BANK_SORT_COLUMNS.DATE ? DESCENDING : ASCENDING
  }`;
}

const GROUP_FOLDERS = 0;
const GROUP_PROJECTS = 1;
const GROUP_FILES = 2;

const getGroupRank = (item) => {
  switch (item.kind) {
    case BANK_ITEM_KINDS.FOLDER:
    case BANK_ITEM_KINDS.VIRTUAL_FOLDER:
    case BANK_ITEM_KINDS.TILAAJAVASTUU:
      return GROUP_FOLDERS;
    case BANK_ITEM_KINDS.PROJECT:
      return GROUP_PROJECTS;
    default:
      return GROUP_FILES;
  }
};

const FORMAT_RANK = BANK_FORMAT_KEYS.reduce((map, key, index) => {
  map[key] = index;
  return map;
}, {});

const compareNames = (a, b) =>
  (a.displayName || '').localeCompare(b.displayName || '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const compareNumbers = (a, b) => (a === b ? 0 : a < b ? -1 : 1);

const compareStrings = (a, b) => (a === b ? 0 : a < b ? -1 : 1);

// Returns a direction-aware comparator where "empty" values (null, 0, '')
// sort last in both directions.
const withEmptyLast = (getValue, isEmpty, compare, direction) => (a, b) => {
  const valueA = getValue(a);
  const valueB = getValue(b);
  const emptyA = isEmpty(valueA);
  const emptyB = isEmpty(valueB);
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;
  const result = compare(valueA, valueB);
  return direction === DESCENDING ? -result : result;
};

const compareDefault = (a, b) => compareNumbers(b.createdAtMs, a.createdAtMs);

const compareType = (a, b) => {
  const rankDiff = compareNumbers(
    FORMAT_RANK[a.formatKey] ?? FORMAT_RANK.other,
    FORMAT_RANK[b.formatKey] ?? FORMAT_RANK.other
  );
  if (rankDiff !== 0) return rankDiff;
  const extensionDiff = compareStrings(a.extension || '', b.extension || '');
  if (extensionDiff !== 0) return extensionDiff;
  return compareNames(a, b);
};

const getAuthorName = (item) =>
  item.author && typeof item.author.name === 'string'
    ? item.author.name.toLowerCase()
    : '';

const getWithinGroupComparator = (sortBy) => {
  const parsed = parseSortBy(sortBy);
  if (!parsed) return compareDefault;
  const { column, direction } = parsed;
  const sign = direction === DESCENDING ? -1 : 1;
  switch (column) {
    case BANK_SORT_COLUMNS.NAME:
      return (a, b) => sign * compareNames(a, b);
    case BANK_SORT_COLUMNS.DATE:
      return withEmptyLast(
        (item) => item.createdAtMs,
        (value) => !value,
        compareNumbers,
        direction
      );
    case BANK_SORT_COLUMNS.CREATOR:
      return withEmptyLast(
        getAuthorName,
        (value) => value === '',
        compareStrings,
        direction
      );
    case BANK_SORT_COLUMNS.TYPE:
      return (a, b) => sign * compareType(a, b);
    case BANK_SORT_COLUMNS.SIZE:
      return withEmptyLast(
        (item) => item.size,
        (value) => value === null || value === undefined,
        compareNumbers,
        direction
      );
    default:
      return compareDefault;
  }
};

/**
 * Sorts BankItems into a new array. Folders (incl. virtual) always come
 * first, then sub-projects, then files; `sortBy` orders within each group
 * ('' or unknown = newest first). Stable: ties keep their input order.
 * @param {Array<Object>} items BankItems
 * @param {string} [sortBy] one of BANK_SORT_OPTIONS keys
 * @returns {Array<Object>} new sorted array
 */
export function sortBankItems(items, sortBy) {
  const list = Array.isArray(items) ? items : [];
  const compareWithinGroup = getWithinGroupComparator(sortBy);
  return list
    .map((item, index) => ({ item, index, group: getGroupRank(item) }))
    .sort((a, b) => {
      if (a.group !== b.group) return a.group - b.group;
      const result = compareWithinGroup(a.item, b.item);
      return result !== 0 ? result : a.index - b.index;
    })
    .map((entry) => entry.item);
}
